import { useEffect } from 'react';
import { IS_NATIVE } from '@/lib/nativeBridge';
import {
  GAMING_HUB_MODAL_OPEN_ATTRIBUTE,
  GAMING_HUB_MODAL_OPEN_EVENT,
} from '@/hooks/useGamingHubModalOpen';
import headerHtml from '@/dre/header-pre.html?raw';
import footerHtml from '@/dre/footer-pre.html?raw';

const DRE_AD_SCRIPT_PATHS = new Set(['/prebid.dreamteamfc.min.js', '/ads.dreamteamfc.min.js']);

function isDreAdLibraryScript(script: HTMLScriptElement) {
  const src = script.getAttribute('src');
  if (!src) return false;

  try {
    const url = new URL(src, window.location.href);
    return url.hostname === 'ads.dreamteamfc.com' && DRE_AD_SCRIPT_PATHS.has(url.pathname);
  } catch {
    return false;
  }
}

// innerHTML does not execute <script> tags; clone them as real script
// elements so the DRE template's bootstrap script runs.
function reactivateScripts(root: HTMLElement) {
  root.querySelectorAll('script').forEach(oldScript => {
    if (isDreAdLibraryScript(oldScript)) {
      oldScript.remove();
      return;
    }

    const fresh = document.createElement('script');
    for (const attr of Array.from(oldScript.attributes)) {
      fresh.setAttribute(attr.name, attr.value);
    }
    fresh.text = oldScript.text;
    oldScript.parentNode?.replaceChild(fresh, oldScript);
  });
}

function ensureBodySlot(
  slot: 'header' | 'footer' | 'webcomponent',
  insert: (host: HTMLElement) => void
) {
  const selector = `[data-dre-slot="${slot}"]`;
  if (document.querySelector(selector)) return;
  const host = document.createElement('div');
  host.setAttribute('data-dre-slot', slot);
  if (slot === 'header') {
    document.body.insertBefore(host, document.body.firstChild);
  } else {
    document.body.appendChild(host);
  }
  insert(host);
}

function injectFragment(host: HTMLElement, html: string) {
  host.innerHTML = html;
  reactivateScripts(host);
}

function injectWebComponent(host: HTMLElement) {
  const ghc = document.createElement('g-h-c');
  ghc.setAttribute('language', 'en');
  ghc.setAttribute('game', 'dtfantasy');
  ghc.setAttribute('hiddenMode', 'true');
  ghc.setAttribute('competitionId', import.meta.env.VITE_ENGAGECRAFT_COMPETITION_ID || '72');
  ghc.setAttribute('season', 'current');
  host.appendChild(ghc);
}

// GH auth must be mounted before GhAuthProvider releases routed layouts.
// Otherwise the provider waits for a global that the routed DreShell has not
// had a chance to create yet.
export function GamingHubAuthHost() {
  useEffect(() => {
    if (IS_NATIVE) return;
    ensureBodySlot('webcomponent', injectWebComponent);
  }, []);

  return null;
}

function hasGamingHubModalContent(host: HTMLElement | null) {
  return !!host && host.childElementCount > 0;
}

function watchGamingHubModal() {
  if (typeof MutationObserver === 'undefined') {
    return () => {};
  }

  let modalObserver: MutationObserver | undefined;
  let observedHost: HTMLElement | null = null;

  const updateState = () => {
    const host = document.getElementById('ghcModalView');

    if (host !== observedHost) {
      modalObserver?.disconnect();
      observedHost = host;

      if (host) {
        modalObserver = new MutationObserver(updateState);
        modalObserver.observe(host, {
          attributes: true,
          childList: true,
          subtree: true,
        });
      }
    }

    const isOpen = hasGamingHubModalContent(host);
    const wasOpen = document.body.hasAttribute(GAMING_HUB_MODAL_OPEN_ATTRIBUTE);

    document.body.toggleAttribute(GAMING_HUB_MODAL_OPEN_ATTRIBUTE, isOpen);
    if (wasOpen !== isOpen) {
      window.dispatchEvent(new Event(GAMING_HUB_MODAL_OPEN_EVENT));
    }
  };

  const bodyObserver = new MutationObserver(updateState);
  bodyObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });
  updateState();

  return () => {
    bodyObserver.disconnect();
    modalObserver?.disconnect();
    document.body.removeAttribute(GAMING_HUB_MODAL_OPEN_ATTRIBUTE);
    window.dispatchEvent(new Event(GAMING_HUB_MODAL_OPEN_EVENT));
  };
}

// Injects the DRE header/footer fragments and the Gaming Hub auth web
// component as direct children of <body> (per the DRE integration spec).
// Keeping them outside the React #root container prevents our app's
// stacking context from occluding consent / menu modals from the template.
export function DreShell({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    if (IS_NATIVE) return;
    ensureBodySlot('webcomponent', injectWebComponent);
    ensureBodySlot('header', host => injectFragment(host, headerHtml));
    ensureBodySlot('footer', host => injectFragment(host, footerHtml));

    return watchGamingHubModal();
  }, []);

  return <>{children}</>;
}
